import axios from "axios";
import { rootUrl } from "../rootUrl";
import { Campaign } from "./adminCampaign";

export interface Message {
  role: "user" | "assistant";
  content: string;
}

const buildAuthHeaders = (token: string) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

export const streamChat = async (
  token: string,
  messages: Message[],
  clientId: string,
  campaign: Campaign | null | undefined,
  onChunk: (text: string) => void,
  onComplete: () => void,
  onError: (error: string) => void
) => {
  try {
    const response = await fetch(`${rootUrl}/admin/ai/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        messages,
        clientId,
        campaign: campaign ? {
          id: campaign.id,
          name: campaign.name,
          description: campaign.description,
          goalAmount: campaign.goalAmount,
          expires: campaign.expires,
          tagline: campaign.tagline,
        } : undefined
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to connect to AI");
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error("No response reader");
    }

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split("\n");

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const data = line.slice(6);
          if (data === "[DONE]") {
            onComplete();
            return;
          }

          try {
            const parsed = JSON.parse(data);
            if (parsed.text) {
              onChunk(parsed.text);
            } else if (parsed.error) {
              onError(parsed.error);
              return;
            }
          } catch (e) {
            // Ignore parsing errors for malformed chunks
          }
        }
      }
    }
  } catch (error: any) {
    onError(error.message || "Unknown error");
  }
};

export const getChatMessages = async (
  token: string,
  campaignId: string
): Promise<Message[]> => {
  const { data } = await axios.get(
    `${rootUrl}/admin/campaign/${campaignId}/chat-messages`,
    buildAuthHeaders(token)
  );
  return data;
};

export const saveChatMessage = async (
  token: string,
  campaignId: string,
  role: string,
  content: string
): Promise<any> => {
  const { data } = await axios.post(
    `${rootUrl}/admin/campaign/${campaignId}/chat-messages`,
    { role, content },
    buildAuthHeaders(token)
  );
  return data;
};
